/**
 * Characters imported from Battle.net: the regions and equipment slots Blizzard's Profile API
 * uses, the snapshot shapes stored per character, the admin-set import settings, and the views
 * the character pages and `/me` render.
 */
import { z } from 'zod';
import type { DataStatus } from './artifact-types.js';

export const CHARACTER_REGIONS = ['us', 'eu', 'kr', 'tw'] as const;
export type CharacterRegion = (typeof CHARACTER_REGIONS)[number];
export const CHARACTER_REGION_LABELS: Record<CharacterRegion, string> = {
  us: 'US',
  eu: 'EU',
  kr: 'KR',
  tw: 'TW',
};

/** Blizzard's `slot.type` values in the order a character sheet lists them. */
export const CHARACTER_SLOT_TYPES = [
  'HEAD',
  'NECK',
  'SHOULDER',
  'BACK',
  'CHEST',
  'SHIRT',
  'TABARD',
  'WRIST',
  'HANDS',
  'WAIST',
  'LEGS',
  'FEET',
  'FINGER_1',
  'FINGER_2',
  'TRINKET_1',
  'TRINKET_2',
  'MAIN_HAND',
  'OFF_HAND',
  'RANGED',
  'AMMO',
] as const;
export type CharacterSlotType = (typeof CHARACTER_SLOT_TYPES)[number];
export const CHARACTER_SLOT_LABELS: Record<CharacterSlotType, string> = {
  HEAD: 'Head',
  NECK: 'Neck',
  SHOULDER: 'Shoulder',
  BACK: 'Back',
  CHEST: 'Chest',
  SHIRT: 'Shirt',
  TABARD: 'Tabard',
  WRIST: 'Wrist',
  HANDS: 'Hands',
  WAIST: 'Waist',
  LEGS: 'Legs',
  FEET: 'Feet',
  FINGER_1: 'Finger 1',
  FINGER_2: 'Finger 2',
  TRINKET_1: 'Trinket 1',
  TRINKET_2: 'Trinket 2',
  MAIN_HAND: 'Main Hand',
  OFF_HAND: 'Off Hand',
  RANGED: 'Ranged',
  AMMO: 'Ammo',
};

/** The optimizer's slot names; shirt, tabard and ammo have none. */
export type BuildSlot =
  | 'head'
  | 'neck'
  | 'shoulder'
  | 'back'
  | 'chest'
  | 'wrist'
  | 'hands'
  | 'waist'
  | 'legs'
  | 'feet'
  | 'finger1'
  | 'finger2'
  | 'trinket1'
  | 'trinket2'
  | 'mainHand'
  | 'offHand'
  | 'ranged';
const SLOT_TO_BUILD: Partial<Record<CharacterSlotType, BuildSlot>> = {
  HEAD: 'head',
  NECK: 'neck',
  SHOULDER: 'shoulder',
  BACK: 'back',
  CHEST: 'chest',
  WRIST: 'wrist',
  HANDS: 'hands',
  WAIST: 'waist',
  LEGS: 'legs',
  FEET: 'feet',
  FINGER_1: 'finger1',
  FINGER_2: 'finger2',
  TRINKET_1: 'trinket1',
  TRINKET_2: 'trinket2',
  MAIN_HAND: 'mainHand',
  OFF_HAND: 'offHand',
  RANGED: 'ranged',
};
export function characterSlotToBuildSlot(slot: CharacterSlotType): BuildSlot | null {
  return SLOT_TO_BUILD[slot] ?? null;
}
const BUILD_TO_SLOT = Object.fromEntries(Object.entries(SLOT_TO_BUILD).map(([c, b]) => [b, c])) as Record<BuildSlot, CharacterSlotType>;
/** Where a build slot sits on the character sheet. */
export function buildSlotToCharacterSlot(slot: BuildSlot): CharacterSlotType {
  return BUILD_TO_SLOT[slot];
}

export const CHARACTER_DETAIL_STATUSES = ['pending', 'ready', 'failed'] as const;
export type CharacterDetailStatus = (typeof CHARACTER_DETAIL_STATUSES)[number];

/** Characters imported per account import (highest level first); the rest are ignored. */
export const CHARACTER_IMPORT_MAX = 60;

/** `/me?import=` outcomes after the Battle.net round-trip. */
export const CHARACTER_IMPORT_OUTCOMES = ['ok', 'none', 'denied', 'failed', 'mismatch', 'too_many'] as const;
export type CharacterImportOutcome = (typeof CHARACTER_IMPORT_OUTCOMES)[number];

const shortText = z.string().max(200);

/** Admin-set import settings (`site_settings` key `characters`). */
export const characterSettingsSchema = z.object({
  /** The token between `profile-` and `-{region}` in Blizzard's profile namespace. */
  namespace: z
    .string()
    .regex(/^[a-z0-9]{2,32}$/)
    .default('classic1x'),
  /** The game version shown to players ("Imports from Classic Era"). */
  label: z.string().min(1).max(40).default('Classic Era'),
});
export type CharacterSettings = z.infer<typeof characterSettingsSchema>;

export const characterEquipmentSchema = z.array(
  z.object({
    slot: z.enum(CHARACTER_SLOT_TYPES),
    itemId: z.number().int().min(0),
    name: shortText,
    quality: shortText.optional(),
    itemLevel: z.number().int().min(0).optional(),
    enchantmentId: z.number().int().min(0).optional(),
    enchantText: shortText.optional(),
  }),
);
export type CharacterEquipment = z.infer<typeof characterEquipmentSchema>;

export const characterTalentsSchema = z.object({
  specialization: shortText.optional(),
  talents: z.array(
    z.object({
      talentId: z.number().int().min(0),
      spellId: z.number().int().min(0),
      rank: z.number().int().min(0),
      name: shortText,
    }),
  ),
});
export type CharacterTalents = z.infer<typeof characterTalentsSchema>;

export const characterPublicBodySchema = z.object({ public: z.boolean() });

export function characterUrl(c: { region: string; realmSlug: string; name: string }): string {
  return `/character/${c.region}/${c.realmSlug}/${encodeURIComponent(c.name.toLowerCase())}`;
}

export interface CharacterSummary {
  id: string;
  region: CharacterRegion;
  realmSlug: string;
  realmName: string;
  name: string;
  level: number;
  class: string;
  race: string;
  faction: string;
  public: boolean;
  detailStatus: CharacterDetailStatus;
  error: string | null;
  importedAt: string;
  url: string;
}

export interface CharacterGearItem {
  name: string;
  artifactId?: string;
  slug?: string;
  dataStatus?: DataStatus;
  sourceBuild?: string | null;
  iconUrl?: string;
  /** Item quality 0-6 (colours the slot's border, as the game does). */
  quality?: number;
  itemLevel?: number;
  /** The database's tooltip numbers, summed into the sheet's attribute panel. */
  stats?: { name: string; value: number }[];
  armor?: number;
  damage?: { min: number; max: number; speed: number };
}

/** The sheet's attribute panel: the sum of what the resolved items grant. */
export interface CharacterAttributes {
  strength: number;
  agility: number;
  stamina: number;
  intellect: number;
  spirit: number;
  armor: number;
  /** Items the database knew, out of those equipped; the panel says when the sum is partial. */
  itemsCounted: number;
  itemsEquipped: number;
}

const ATTRIBUTE_STATS: Record<string, keyof Omit<CharacterAttributes, 'armor' | 'itemsCounted' | 'itemsEquipped'>> = {
  Strength: 'strength',
  Agility: 'agility',
  Stamina: 'stamina',
  Intellect: 'intellect',
  Spirit: 'spirit',
};

/** Sum the attribute panel from the equipped items the database knows. */
export function sumAttributes(gear: Pick<CharacterGearRow, 'item'>[]): CharacterAttributes {
  const out: CharacterAttributes = { strength: 0, agility: 0, stamina: 0, intellect: 0, spirit: 0, armor: 0, itemsCounted: 0, itemsEquipped: 0 };
  for (const row of gear) {
    if (!row.item) continue;
    out.itemsEquipped++;
    if (!row.item.artifactId) continue;
    out.itemsCounted++;
    out.armor += row.item.armor ?? 0;
    for (const s of row.item.stats ?? []) {
      const key = ATTRIBUTE_STATS[s.name];
      if (key) out[key] += s.value;
    }
  }
  return out;
}

export interface CharacterGearRow {
  slot: CharacterSlotType;
  label: string;
  item: CharacterGearItem | null;
  enchant?: { id: number; text: string; recipeSlug?: string };
  /** The game's empty-slot art for this slot (client media). */
  slotArtUrl: string;
}

export interface CharacterTalentRow {
  name: string;
  /** Points the character has in it (0 for a talent of the tree it has not taken). */
  rank: number;
  maxRank: number;
  artifactId?: string;
  slug?: string;
  /** Position in the talent window's grid (1-based), known for talents the database has. */
  tier?: number;
  column?: number;
  iconUrl?: string;
  /** Tooltip text for the taken rank (or rank 1 when none). */
  description?: string;
  /** The talent this one needs first, at that rank; the window draws an arrow between them. */
  prerequisite?: { artifactId: string; rank: number };
}

export interface CharacterTalentTree {
  tree: string;
  /** Points spent in the tree. */
  points: number;
  /** The tree icon from the client's talent tab, absent for the catch-all tree. */
  iconUrl?: string;
  /** The tree's in-game background art (client media), absent for the catch-all tree. */
  backgroundUrl?: string;
  /** Every talent of the tree the database knows plus any taken talent it does not. */
  talents: CharacterTalentRow[];
}

export interface CharacterView extends CharacterSummary {
  gender: string;
  guild: string | null;
  namespace: string;
  label: string;
  gear: CharacterGearRow[];
  attributes: CharacterAttributes;
  /** The class icon the seed copied into image storage (absent until a seed has run). */
  classIconUrl: string;
  /** The class's trees in the game's order (each with all its talents), then an "Other" tree for taken talents the database lacks. */
  talents: { specialization: string | null; trees: CharacterTalentTree[] };
  ownerBattletag: string;
  mine: boolean;
}

/** The compact snapshot the optimizer reads. */
export interface CharacterSnapshotView {
  id: string;
  class: string;
  race: string;
  faction: string;
  level: number;
  equipped: { slot: BuildSlot; artifactId?: string; name: string; itemLevel?: number; enchant?: { id: number; artifactId?: string; text: string } }[];
  talents: { artifactId?: string; name: string; rank: number }[];
}

export interface MeCharactersResponse {
  characters: CharacterSummary[];
  settings: CharacterSettings;
  importing: boolean;
}

export interface CharactersListResponse {
  page: number;
  pageSize: number;
  total: number;
  items: CharacterSummary[];
}

export interface CharacterRealm {
  region: CharacterRegion;
  realmSlug: string;
  realmName: string;
  count: number;
}
